import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, timeout, defer, map, catchError, of, throwError as observableThrowError } from 'rxjs';
import { FareRequest } from '../../dtos/request/pricing-api/fare-request';
import { FareQuoteRequest } from '../../dtos/request/pricing-api/fare-quote-request';
import { ExpandedPricingResponse } from '../../dtos/response/pricing-api/expanded-pricing-response';
import { FareQuotePricingResponse } from '../../dtos/response/pricing-api/fare-quote-pricing-response';
import { FareResponse } from '../../dtos/response/pricing-api/fare-response';
import { FareQuoteResponse } from '../../dtos/response/pricing-api/fare-quote-response';
import { timeoutError } from '../../models/timeout-error';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { TimeoutLimit } from '../../models/timeout-limit';
import { ReshopFareRequest } from '../../dtos/request/pricing-api/reshop-fare-request';
import { ReshopFareResponseWrapper } from '../../dtos/response/pricing-api/reshop/reshop-fare-response-wrapper';
import { ReshopFareResponse } from '../../dtos/response/pricing-api/reshop/reshop-fare-response';
import { ReshopQuoteFareRequest } from '../../dtos/request/pricing-api/reshop-quote-fare-request';

@Injectable({
  providedIn: 'root',
})
export class PricingApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  getPricingApiFare(request: FareRequest, background: boolean): Observable<FareResponse> {
    return this.makeApiRequest<ExpandedPricingResponse, FareResponse>('api/price/shopping/fare', request, background);
  }

  getReshopFare(request: ReshopFareRequest, background: boolean): Observable<ReshopFareResponseWrapper> {
    return this.makeApiRequest<ReshopFareResponse, ReshopFareResponseWrapper>('api/price/reshop/fare', request, background);
  }

  getFareQuote(request: FareQuoteRequest, background: boolean): Observable<FareQuoteResponse> {
    return this.makeApiRequest<FareQuotePricingResponse, FareQuoteResponse>('api/price/quote/fare', request, background);
  }

  getReshopQuoteFare(request: ReshopQuoteFareRequest, background: boolean): Observable<ReshopFareResponseWrapper> {
    return this.makeApiRequest<ReshopFareResponse, ReshopFareResponseWrapper>('api/price/reshop/quote', request, background);
  }

  private makeApiRequest<TResponse, TWrappedResponse>(endpoint: string, request: any, background: boolean): Observable<TWrappedResponse> {
    const options = {
      headers: new HttpHeaders({
        background: background ? 'true' : 'false',
      }),
    };
    return this.httpClient.post<TResponse>(endpoint, request, options).pipe(
      timeout({
        each: TimeoutLimit.MEDIUM,
        with: () => defer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => {
        return { success: true, response } as TWrappedResponse;
      }),
      catchError((err: HttpErrorResponse) => {
        if (timeoutError.status === err.status) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return of({
            success: false,
            exceptionContent: 'Timeout',
          } as TWrappedResponse);
        } else if (err.error?.error) {
          // Error from API, just use the error message
          return of({
            success: false,
            exceptionContent: err.error.error?.message,
          } as TWrappedResponse);
        } else {
          // Uncaught error, use the error object as a string
          return of({
            success: false,
            exceptionContent: err.error ? JSON.stringify(err.error) : 'Uncaught',
          } as TWrappedResponse);
        }
      })
    );
  }
}
